/**
 * Feature-Flagged Debug Logger
 *
 * Provides categorized console logging that can be toggled at runtime
 * via Control Plane UI → Redis → WebSocket broadcast.
 *
 * Categories:
 * - jitsi: Video conference (Jitsi, media status, speaking indicators)
 * - websocket: Real-time transport (connections, heartbeat, pub/sub)
 * - session: Session/breakout management (repos, assignments, join/leave)
 * - collaboration: Yjs document sync (recovery, compaction)
 * - infrastructure: Cross-cutting (auth, storage, telemetry, migrations)
 * - hand-raise-debug: Dedicated hand raise troubleshooting flag
 *
 * Usage:
 *   import { createLogger } from '@/lib/logger';
 *   const logger = createLogger('[HandRaise]', 'hand-raise-debug');
 *   logger.log('par_123: raised=true');    // Only if hand-raise-debug enabled
 *   logger.error('Failed:', error);         // Always shows (safety net)
 *
 * Default state:
 * - Development (NODE_ENV=development): All enabled except hand-raise-debug
 * - Production: All disabled (opt-in via Control Plane)
 *
 * @see TWA-project/kb/specs/exercise-master-template.md - Architecture docs
 */

// ============================================================================
// Types
// ============================================================================

export type LogCategory =
  | 'jitsi'
  | 'websocket'
  | 'session'
  | 'collaboration'
  | 'infrastructure'
  | 'modules'
  | 'hand-raise-debug'
  | 'roster-integrity'
  | 'identity';

export interface LogSettings {
  jitsi: boolean;
  websocket: boolean;
  session: boolean;
  collaboration: boolean;
  infrastructure: boolean;
  modules: boolean;
  handRaiseDebug: boolean;
  rosterIntegrity: boolean;
  identity: boolean;
}

/** Map from LogCategory to LogSettings key */
const CATEGORY_TO_KEY: Record<LogCategory, keyof LogSettings> = {
  'jitsi': 'jitsi',
  'websocket': 'websocket',
  'session': 'session',
  'collaboration': 'collaboration',
  'infrastructure': 'infrastructure',
  'modules': 'modules',
  'hand-raise-debug': 'handRaiseDebug',
  'roster-integrity': 'rosterIntegrity',
  'identity': 'identity',
};

// ============================================================================
// Global State
// ============================================================================

let currentLogSettings: LogSettings = getDefaultLogSettings();

export function getDefaultLogSettings(): LogSettings {
  const isDev =
    typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
  return {
    jitsi: isDev,
    websocket: isDev,
    session: isDev,
    collaboration: isDev,
    infrastructure: isDev,
    modules: isDev,
    handRaiseDebug: false, // Always off by default (opt-in only)
    // Temporary investigation category (2026-07-25 audit F5 — duplicate
    // attendee rows). Deliberately defaulted ON in production, unlike other
    // temporary categories: the condition it traces is rare, transient, and
    // leaves no durable evidence, so it has to be armed BEFORE the next class
    // rather than switched on after someone notices. The tripwire itself logs
    // at error level (never gated); this flag only controls the verbose trace.
    // Remove this category once the minting event is identified and fixed.
    rosterIntegrity: true,
    // Session-identity shadow mode (P0). Defaulted ON in production for the same
    // reason as rosterIntegrity above: this category exists to MEASURE, across at
    // least one real class, whether the new session-role resolver would have decided
    // anything differently from the nine legacy mechanisms. Evidence that has to be
    // switched on after someone notices is evidence that was never collected — and
    // P3 onward is gated on it (`00-MASTER-PLAN.md` § 5a).
    //
    // It is cheap to leave on: only DISAGREEMENTS are logged, never agreements, and
    // shadow mode changes no outcome. Turn it off once the P0 gate has been read.
    identity: true,
  };
}

export function getLogSettings(): LogSettings {
  return { ...currentLogSettings };
}

export function updateLogSettings(settings: Partial<LogSettings>): void {
  currentLogSettings = { ...currentLogSettings, ...settings };
}

// ============================================================================
// Logger Factory
// ============================================================================

export interface Logger {
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
}

/**
 * Create a categorized logger instance.
 *
 * @param prefix - Log prefix (e.g., '[HandRaise]', '[Heartbeat]')
 * @param category - Log category for feature flag filtering
 * @returns Logger object with log/warn/error/debug methods
 */
export function createLogger(prefix: string, category: LogCategory): Logger {
  const settingsKey = CATEGORY_TO_KEY[category];

  return {
    log: (...args: unknown[]) => {
      if (currentLogSettings[settingsKey]) {
        console.log(prefix, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      if (currentLogSettings[settingsKey]) {
        console.warn(prefix, ...args);
      }
    },
    error: (...args: unknown[]) => {
      // Errors always show regardless of settings (safety net)
      console.error(prefix, ...args);
    },
    debug: (...args: unknown[]) => {
      if (currentLogSettings[settingsKey]) {
        console.debug(prefix, ...args);
      }
    },
  };
}
